import React, { useState, useEffect } from 'react';
import { RefreshCw, X, Sparkles } from 'lucide-react';

// Detects new deployments two ways:
// 1. Service worker update (sw-update-available event)
// 2. Polling index.html for a changed main JS bundle hash (catches every production deploy)
export const UpdateBanner = () => {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [assetUpdate, setAssetUpdate] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const onUpdate = (e) => setWaitingWorker(e.detail);
    window.addEventListener('sw-update-available', onUpdate);
    return () => window.removeEventListener('sw-update-available', onUpdate);
  }, []);

  useEffect(() => {
    let initial = null;
    let timer = null;
    const signature = async () => {
      try {
        const res = await fetch('/index.html', { cache: 'no-store' });
        const text = await res.text();
        const m = text.match(/src="([^"]*main[^"]*\.js)"/);
        return m ? m[1] : null;
      } catch { return null; }
    };
    (async () => {
      initial = await signature();
      if (!initial) return;
      timer = setInterval(async () => {
        const current = await signature();
        if (current && current !== initial) {
          setAssetUpdate(true);
          clearInterval(timer);
        }
      }, 120000);
    })();
    return () => timer && clearInterval(timer);
  }, []);

  if ((!waitingWorker && !assetUpdate) || dismissed) return null;

  const refresh = () => {
    setRefreshing(true);
    try { if (waitingWorker) waitingWorker.postMessage({ type: 'SKIP_WAITING' }); } catch { /* noop */ }
    setTimeout(() => window.location.reload(), waitingWorker ? 1500 : 300);
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] w-[92%] max-w-md rounded-xl shadow-2xl bg-slate-900 text-white px-4 py-3 flex items-center gap-3 animate-in slide-in-from-bottom-4"
      data-testid="update-banner"
    >
      <Sparkles className="w-5 h-5 text-amber-300 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-tight">New update &amp; enhanced features available</p>
        <p className="text-[11px] text-slate-300 leading-tight">Please refresh to get the new features of JMEPL Support.</p>
      </div>
      <button
        onClick={refresh}
        disabled={refreshing}
        className="shrink-0 inline-flex items-center gap-1.5 bg-amber-400 hover:bg-amber-300 text-slate-900 text-xs font-bold rounded-full px-3.5 py-2 transition-colors disabled:opacity-60"
        data-testid="update-banner-refresh-btn"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        {refreshing ? 'Updating…' : 'Refresh Now'}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="shrink-0 text-slate-400 hover:text-white transition-colors"
        aria-label="Dismiss"
        data-testid="update-banner-dismiss-btn"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
